/**
 * One shape for "what went wrong with this request" (F19, F23).
 *
 * The UI used to collapse every failure into a single string, so three very
 * different situations read the same: a session that expired (sign in again),
 * a permission the user lacks (ask an admin for it by name), and a backend or
 * third party that failed (retry, quote the reference). The backend already says
 * which one it is:
 *
 *   401                                   → auth dependency rejected the credential
 *   403 {detail: "Session expired. ..."}  → core/csrf.py: no/unknown session_id cookie
 *   403 {detail: "CSRF token missing..."} / "Invalid CSRF token..." → core/csrf.py
 *   403 {detail: {code: "permission_denied", permission: "tenant.write"}} → core/authorization.py
 *   403 "Permission denied: X required"   → older string form of the same
 *   5xx {correlation_id} + X-Request-ID   → main.py global handler / internal_error_detail
 *
 * Pure: no React, no store, so it is testable and usable from the api-client.
 */

export type ApiErrorKind =
  | 'session_expired'
  | 'permission_denied'
  | 'policy_denied'
  | 'dependency_failure'
  | 'validation'
  | 'not_found'
  | 'conflict'
  | 'quota'
  | 'rate_limited'
  | 'network'
  | 'unknown';

export interface ApiErrorInfo {
  kind: ApiErrorKind;
  status: number | null;
  /** What the server said, cleaned up. */
  message: string;
  /** What the user can do about it, in one plain sentence. */
  remediation: string;
  /** The server's correlation id for this request, when it gave one. */
  requestId: string | null;
  /** The permission that was missing, for permission_denied. */
  permission: string | null;
  /** The operation the caller was attempting ("Load integrations"). */
  operation: string | null;
  /** True when the session (not the user's rights) is the problem: sign in, then retry. */
  reauthenticate: boolean;
  /** True when retrying the same request later could succeed. */
  retryable: boolean;
}

const SESSION_403 = [
  /session expired/i,
  /csrf token missing/i,
  /invalid csrf token/i,
];

function header(error: any, name: string): string | null {
  const h = error?.response?.headers;
  if (!h) return null;
  const v = typeof h.get === 'function' ? h.get(name) : (h[name] ?? h[name.toLowerCase()]);
  return typeof v === 'string' && v && v !== 'unknown' ? v : null;
}

/** The correlation id from the X-Request-ID header or the body, if any. */
export function requestIdOf(error: any): string | null {
  const data = error?.response?.data;
  const fromBody = data && typeof data === 'object'
    ? (data.correlation_id || data.request_id || (data.detail && typeof data.detail === 'object' ? (data.detail.correlation_id || data.detail.request_id) : null))
    : null;
  if (typeof fromBody === 'string' && fromBody && fromBody !== 'unknown') return fromBody;
  const fromHeader = header(error, 'x-request-id');
  if (fromHeader) return fromHeader;
  // internal_error_detail() puts "Reference: <id>" inside the detail string.
  const detail = typeof data?.detail === 'string' ? data.detail : '';
  const m = detail.match(/Reference:\s*([A-Za-z0-9-]{6,})/);
  return m ? m[1] : null;
}

function detailText(data: any): string {
  if (!data) return '';
  const d = data.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    return String(d.message || d.error || d.msg || '');
  }
  if (Array.isArray(d) && d[0]?.msg) return String(d[0].msg);
  return String(data.message || data.error || '');
}

/** True when a 403 is the CSRF/session check, not an authorization decision. */
export function isSessionFailure(status: number | null | undefined, data: any): boolean {
  if (status === 401) return true;
  if (status !== 403) return false;
  const text = detailText(data);
  return SESSION_403.some(rx => rx.test(text));
}

function permissionOf(data: any, text: string): string | null {
  const d = data?.detail;
  if (d && typeof d === 'object' && typeof d.permission === 'string') return d.permission;
  const m = text.match(/Permission denied:\s*([a-z0-9_.:-]+)/i) || text.match(/\b(?:needs?|requires?)\s+(?:the\s+)?([a-z0-9_]+\.[a-z0-9_.]+)\s+permission/i);
  return m ? m[1] : null;
}

/** Classify an axios-style error (or anything thrown) into one ApiErrorInfo. */
export function classifyApiError(error: any, operation?: string | null): ApiErrorInfo {
  const status: number | null = typeof error?.response?.status === 'number' ? error.response.status : null;
  const data = error?.response?.data;
  const raw = detailText(data) || (typeof error?.message === 'string' ? error.message : '');
  const requestId = requestIdOf(error);
  const base = { status, requestId, operation: operation || null, permission: null as string | null, reauthenticate: false, retryable: false };

  if (!error?.response) {
    const timedOut = error?.code === 'ECONNABORTED';
    return { ...base, kind: 'network',
      message: timedOut ? 'The request timed out.' : 'The server could not be reached.',
      remediation: 'Check your connection, then try again.', retryable: true };
  }

  if (isSessionFailure(status, data)) {
    return { ...base, kind: 'session_expired', reauthenticate: true, retryable: true,
      message: 'Your session expired.',
      remediation: 'Sign in again to continue. Your changes on this page are kept.' };
  }

  if (status === 403) {
    const d = data?.detail;
    if (d && typeof d === 'object' && d.code === 'policy_rule_denied') {
      return { ...base, kind: 'policy_denied', message: raw || 'Blocked by policy.',
        remediation: 'A tenant policy blocks this action. Ask a tenant admin to review the policy.' };
    }
    const permission = permissionOf(data, raw);
    return { ...base, kind: 'permission_denied', permission,
      message: permission ? `You need the ${permission} permission.` : (raw || 'You do not have permission to do that.'),
      remediation: permission
        ? `Ask a tenant admin to grant you the ${permission} permission.`
        : 'Ask a tenant admin for access.' };
  }

  if (status === 402) return { ...base, kind: 'quota', message: raw || 'Plan limit reached.', remediation: 'Upgrade the plan or free up capacity.' };
  if (status === 429) return { ...base, kind: 'rate_limited', retryable: true, message: raw || 'Too many requests.', remediation: 'Wait a moment, then try again.' };
  if (status === 404) return { ...base, kind: 'not_found', message: raw || 'Not found.', remediation: 'It may have been removed. Refresh the page.' };
  if (status === 409) return { ...base, kind: 'conflict', message: raw || 'This changed since you loaded it.', remediation: 'Refresh to see the latest version, then try again.' };
  if (status === 400 || status === 422) return { ...base, kind: 'validation', message: raw || 'The request was not valid.', remediation: 'Check the highlighted fields, then try again.' };

  if (status !== null && status >= 500) {
    const generic = !raw || /unexpected error|correlation_id below|could not complete this request/i.test(raw);
    return { ...base, kind: 'dependency_failure', retryable: true,
      message: generic ? 'The server could not complete this request.' : raw.replace(/\s*Reference:\s*\S+\s*$/, ''),
      remediation: requestId
        ? 'Try again. If it keeps failing, contact support and quote the reference below.'
        : 'Try again. If it keeps failing, contact support.' };
  }

  return { ...base, kind: 'unknown', message: raw || 'Something went wrong.', remediation: 'Try again.' };
}

/** Methods whose replay after a refreshed session cannot create a second effect. */
const SAFE_REPLAY_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE']);

/**
 * May this request be replayed automatically once the session is renewed?
 * GET/HEAD/OPTIONS/PUT/DELETE with the same body are idempotent. POST and PATCH
 * only when the caller sent an Idempotency-Key, which the server dedupes on.
 */
export function isSafeToReplay(method: string | undefined, headers: any): boolean {
  const m = (method || 'GET').toUpperCase();
  if (SAFE_REPLAY_METHODS.has(m)) return true;
  if (!headers) return false;
  const get = (k: string) => (typeof headers.get === 'function' ? headers.get(k) : headers[k]);
  const key = get('Idempotency-Key') ?? get('idempotency-key');
  return typeof key === 'string' && key.trim().length > 0;
}
